import { useAuth0 } from '@auth0/auth0-react';
import { lazy, Suspense } from 'react';
import { Routes as BrowserRoutes, Outlet, Route } from 'react-router-dom';

import useAccountData from '@hooks/useAccountData';

import { useEmailMap } from '@hooks/useEmailMap';
import { useClientStatusStore } from '@stores/useClientStatusStore';

import BrowsePage from 'pages/BrowsePage';
import ForbiddenPage, { ForbiddenReason } from 'pages/ForbiddenPage';
import LandingPage from 'pages/LandingPage';
import ServiceUnavailable from 'pages/ServiceUnavailablePage';
import TosPage from 'pages/TosPage';
import ViewPage from 'pages/ViewPage';
import WelcomePage from 'pages/WelcomePage';

const CollectionsPage = lazy(() => import('pages/CollectionsPage'));
const DashboardPage = lazy(() => import('pages/DashboardPage'));
const HomePage = lazy(() => import('pages/HomePage'));
const UnauthorizedPage = lazy(() => import('pages/UnauthorizedPage'));
const EmailNotVerifiedPage = lazy(() => import('pages/EmailNotVerified'));
const NotFoundPage = lazy(() => import('pages/NotFoundPage'));
const RequestsPage = lazy(() => import('pages/RequestsPage'));
const TransferCollectionPage = lazy(() => import('pages/TransferCollectionPage'));
const TransferFilePage = lazy(() => import('pages/TransferFilePage'));
const TransferTokensPage = lazy(() => import('pages/TransferTokensPage'));
const TransferPage = lazy(() => import('pages/TransferPage'));
const UploadPage = lazy(() => import('pages/UploadPage'));
const RegisterPage = lazy(() => import('pages/RegisterPage'));
const PurchaseTokensPage = lazy(() => import('pages/PurchaseTokensPage'));
const CheckoutPage = lazy(() => import('pages/CheckoutPage'));

const AuthenticatedRoute = ({
  isAuthenticated,
  emailVerified,
  children,
}: {
  isAuthenticated: boolean;
  emailVerified?: boolean;
  children?: any;
}) => {
  if (!isAuthenticated) {
    return <UnauthorizedPage />;
  }

  if (!emailVerified) {
    return <EmailNotVerifiedPage />;
  }

  return children ?? <Outlet />;
};

const ProtectedRoute = ({
  forbiddenReason,
  clientIsOnline,
  children,
}: {
  clientIsOnline: boolean | null;
  forbiddenReason: ForbiddenReason;
  children?: any;
}) => {
  if (clientIsOnline === false) {
    return <ServiceUnavailable />;
  }

  if (forbiddenReason) {
    return <ForbiddenPage reason={forbiddenReason} />;
  }

  return children ?? <Outlet />;
};

const Routes = () => {
  const { isAuthenticated, user } = useAuth0();
  const { account } = useAccountData();
  const { map } = useEmailMap(user?.email);
  const { clientIsOnline } = useClientStatusStore();

  const emailVerified = !!user?.email_verified;

  const getForbiddenReason = (): ForbiddenReason => {
    if (!map) {
      return 'unregistered';
    }

    if (map && !account) {
      return 'wallet-locked';
    }

    return undefined;
  };

  return (
    <Suspense fallback={null}>
      <BrowserRoutes>
        <Route path="/" element={<HomePage />} />
        <Route path="/browse" element={<BrowsePage />} />
        <Route path="/view/:id" element={<ViewPage />} />
        <Route path="/welcome" element={<WelcomePage />} />
        <Route path="/landing" element={<LandingPage />} />
        <Route path="/terms-and-conditions" element={<TosPage />} />
        <Route path="*" element={<NotFoundPage />} />

        <Route element={<AuthenticatedRoute isAuthenticated={isAuthenticated} emailVerified={emailVerified} />}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/register" element={<RegisterPage />} />

          <Route element={<ProtectedRoute clientIsOnline={clientIsOnline} forbiddenReason={getForbiddenReason()} />}>
            <Route path="/upload" element={<UploadPage />} />
            <Route path="/transfer" element={<TransferPage />} />
            <Route path="/transfer/tokens" element={<TransferTokensPage />} />
            <Route path="/transfer/file" element={<TransferFilePage />} />
            <Route path="/transfer/collection" element={<TransferCollectionPage />} />
            <Route path="/collections" element={<CollectionsPage />} />
            <Route path="/requests" element={<RequestsPage />} />
            <Route path="/purchase" element={<PurchaseTokensPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
          </Route>
        </Route>
      </BrowserRoutes>
    </Suspense>
  );
};

export default Routes;
